import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { Loader2, Send, MapPin, Phone, Mail, Clock, MessageCircle, Linkedin, Instagram, Facebook, CheckCircle2 } from "lucide-react";

export default function Contato() {
  const { content, config } = useSite();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha os campos obrigatórios.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contacts", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Mensagem enviada!");
    } catch {
      toast.error("Erro ao enviar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contato" className="pt-32 md:pt-40 pb-20 bg-[#f5f7fa] min-h-screen">
      <div className="dc-container">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-dc-primary">Contato</span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-dc-text mt-3 mb-4 leading-tight">Vamos conversar sobre tecnologia</h1>
          <p className="text-base text-slate-600">Estamos prontos para entender seu desafio e propor a melhor solução.</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-4">
            <InfoCard icon={MapPin} title="Endereço" content={content.address} />
            <InfoCard icon={Phone} title="Telefone" content={content.phone} href={`tel:${content.phone}`} />
            <InfoCard icon={Mail} title="E-mail" content={content.email} href={`mailto:${content.email}`} />
            <InfoCard icon={Clock} title="Horário de atendimento" content={content.hours} />

            <div className="bg-gradient-to-br from-dc-secondary to-dc-primary text-white rounded-2xl p-6">
              <MessageCircle className="w-7 h-7 text-dc-accent mb-3" strokeWidth={1.5} />
              <h4 className="font-heading font-semibold text-lg mb-1">Resposta imediata</h4>
              <p className="text-sm text-blue-100 mb-4">Fale com nosso atendimento via WhatsApp.</p>
              <a href={`https://wa.me/${config?.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contato-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1fb557] px-5 py-2.5 rounded-full font-semibold text-sm transition">
                <MessageCircle className="w-4 h-4" /> Iniciar conversa
              </a>
            </div>

            <div className="flex gap-3 pt-2">
              {content.linkedin && <Social href={content.linkedin} icon={Linkedin} />}
              {content.instagram && <Social href={content.instagram} icon={Instagram} />}
              {content.facebook && <Social href={content.facebook} icon={Facebook} />}
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-slate-100">
                <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                <h3 data-testid="contato-success" className="font-heading text-2xl font-bold text-dc-text mb-3">Mensagem enviada!</h3>
                <p className="text-slate-600 mb-6">Obrigado pelo contato. Retornaremos em breve.</p>
                <Button onClick={() => setSuccess(false)} variant="outline" className="rounded-full">Enviar outra mensagem</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl p-8 md:p-10 shadow-xl border border-slate-100 space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Nome *"><Input data-testid="contato-name" value={form.name} onChange={update("name")} placeholder="Seu nome" /></Field>
                  <Field label="E-mail *"><Input data-testid="contato-email" type="email" value={form.email} onChange={update("email")} placeholder="seu@email.com" /></Field>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Telefone"><Input data-testid="contato-phone" value={form.phone} onChange={update("phone")} placeholder="(43) 99999-9999" /></Field>
                  <Field label="Assunto"><Input data-testid="contato-subject" value={form.subject} onChange={update("subject")} placeholder="Sobre o que deseja falar?" /></Field>
                </div>
                <Field label="Mensagem *"><Textarea data-testid="contato-message" value={form.message} onChange={update("message")} rows={6} placeholder="Como podemos ajudar?" /></Field>
                <Button data-testid="contato-submit" type="submit" disabled={loading} className="w-full bg-dc-primary hover:bg-dc-primary-hover text-white py-6 rounded-full font-semibold text-base">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> Enviar mensagem</>}
                </Button>
              </form>
            )}
          </div>
        </div>

        <div className="mt-16 rounded-3xl overflow-hidden border border-slate-100 shadow-lg">
          <iframe
            title="Localização DeskcodeBR"
            src={content.contact_map_embed_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d57058.654!2d-51.21!3d-23.31!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zTG9uZHJpbmEsIFBS!5e0!3m2!1spt-BR!2sbr!4v1700000000000"}
            className="w-full h-[400px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-sm font-medium text-dc-text mb-2 block">{label}</Label>{children}</div>);
const InfoCard = ({ icon: Icon, title, content, href }) => {
  const Content = (
    <div className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-slate-100 hover:shadow-lg hover:border-dc-primary/20 transition-all">
      <div className="w-11 h-11 rounded-xl bg-dc-primary/10 text-dc-primary flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" strokeWidth={1.6} /></div>
      <div><div className="font-heading font-semibold text-dc-text">{title}</div><div className="text-sm text-slate-600">{content}</div></div>
    </div>
  );
  return href ? <a href={href}>{Content}</a> : Content;
};
const Social = ({ href, icon: Icon }) => (
  <a href={href} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white border border-slate-200 hover:bg-dc-primary hover:text-white hover:border-dc-primary text-dc-text flex items-center justify-center transition-all">
    <Icon className="w-4 h-4" />
  </a>
);
